import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CampaignState, CampaignDetailState, CampaignWorkflowState, CampaignImportState } from './campaign.state';
import { CAMPAIGN_FEATURE_KEY } from './campaign.state';

export const selectCampaignFeature = createFeatureSelector<CampaignState>(CAMPAIGN_FEATURE_KEY);

export const selectCampaignList = createSelector(
  selectCampaignFeature,
  (state) => state.list
);

// ─── Standard Campaigns ─────────────────────────────────────────────────────

export const selectStandardCampaigns = createSelector(
  selectCampaignList,
  (list) => list.standardCampaigns
);

export const selectStandardTotal = createSelector(
  selectCampaignList,
  (list) => list.standardTotal
);

export const selectStandardFilteredCount = createSelector(
  selectCampaignList,
  (list) => list.standardFilteredCount
);

export const selectStandardPage = createSelector(
  selectCampaignList,
  (list) => list.standardPage
);

export const selectStandardPageSize = createSelector(
  selectCampaignList,
  (list) => list.standardPageSize
);

export const selectStandardSorting = createSelector(
  selectCampaignList,
  (list) => list.standardSorting
);

// ─── Archived Campaigns ─────────────────────────────────────────────────────

export const selectArchivedCampaigns = createSelector(
  selectCampaignList,
  (list) => list.archivedCampaigns
);

export const selectArchivedTotal = createSelector(
  selectCampaignList,
  (list) => list.archivedTotal
);

export const selectArchivedFilteredCount = createSelector(
  selectCampaignList,
  (list) => list.archivedFilteredCount
);

export const selectArchivedPage = createSelector(
  selectCampaignList,
  (list) => list.archivedPage
);

export const selectArchivedPageSize = createSelector(
  selectCampaignList,
  (list) => list.archivedPageSize
);

export const selectArchivedSorting = createSelector(
  selectCampaignList,
  (list) => list.archivedSorting
);

export const selectStandardStatusFilter = createSelector(
  selectCampaignList,
  (list) => list.standardStatusFilter
);

export const selectArchivedStatusFilter = createSelector(
  selectCampaignList,
  (list) => list.archivedStatusFilter
);

// ─── Shared UI State ────────────────────────────────────────────────────────

export const selectActiveTab = createSelector(
  selectCampaignList,
  (list) => list.activeTab
);

export const selectSearch = createSelector(
  selectCampaignList,
  (list) => list.search
);

export const selectLoading = createSelector(
  selectCampaignList,
  (list) => list.loading
);

export const selectError = createSelector(
  selectCampaignList,
  (list) => list.error
);

// ─── Feature Flags ──────────────────────────────────────────────────────────

export const selectEnableSecureDriveImport = createSelector(
  selectCampaignList,
  (list) => list.enableSecureDriveImport
);

// ─── Derived Selectors ──────────────────────────────────────────────────────

export const selectStandardPagination = createSelector(
  selectStandardPage,
  selectStandardFilteredCount,
  selectStandardPageSize,
  (page, filteredCount, limit) => {
    const totalPages = Math.ceil(filteredCount / limit);
    const pageObject: { pageNum: number; selected: boolean }[] = [];

    if (filteredCount > 1) {
      if (page > 1) {
        pageObject.push({ pageNum: page - 1, selected: false });
      }
      pageObject.push({ pageNum: page, selected: false });
      if (page < totalPages) {
        pageObject.push({ pageNum: page + 1, selected: false });
      }
      if (page === 1) {
        if (pageObject.length > 0) pageObject[0].selected = true;
      } else {
        if (pageObject.length > 1) pageObject[1].selected = true;
      }
    }

    return { page, totalPages, filteredCount, pageObject, limit };
  }
);

export const selectArchivedPagination = createSelector(
  selectArchivedPage,
  selectArchivedFilteredCount,
  selectArchivedPageSize,
  (page, filteredCount, limit) => {
    const totalPages = Math.ceil(filteredCount / limit);
    const pageObject: { pageNum: number; selected: boolean }[] = [];

    if (filteredCount > 1) {
      if (page > 1) {
        pageObject.push({ pageNum: page - 1, selected: false });
      }
      pageObject.push({ pageNum: page, selected: false });
      if (page < totalPages) {
        pageObject.push({ pageNum: page + 1, selected: false });
      }
      if (page === 1) {
        if (pageObject.length > 0) pageObject[0].selected = true;
      } else {
        if (pageObject.length > 1) pageObject[1].selected = true;
      }
    }

    return { page, totalPages, filteredCount, pageObject, limit };
  }
);

// ─── Phase 2: Campaign Detail Selectors ─────────────────────────────────────

export const selectCampaignDetail = createSelector(
  selectCampaignFeature,
  (state) => state.detail
);

export const selectCampaignId = createSelector(
  selectCampaignDetail,
  (detail) => detail.campaignId
);

export const selectCampaignDetails = createSelector(
  selectCampaignDetail,
  (detail) => detail.campaignDetails
);

export const selectIsEditMode = createSelector(
  selectCampaignDetail,
  (detail) => detail.isEditMode
);

export const selectDetailLoading = createSelector(
  selectCampaignDetail,
  (detail) => detail.loading
);

export const selectDetailSaving = createSelector(
  selectCampaignDetail,
  (detail) => detail.saving
);

export const selectDetailError = createSelector(
  selectCampaignDetail,
  (detail) => detail.error
);
export const selectDownloadAuditFileDisabled = createSelector(
  selectCampaignDetail,
  (state) => state.disableAuditListDownload
);

// ─── Campaign Audit History Panel Selectors ─────────────────────────────────

export const selectAuditHistoryState = createSelector(
  selectCampaignFeature,
  (state) => state.auditHistory
);

export const selectAuditHistoryRecords = createSelector(
  selectAuditHistoryState,
  (s) => s.records
);

export const selectAuditHistoryLoading = createSelector(
  selectAuditHistoryState,
  (s) => s.loading
);

export const selectAuditHistoryTotalCount = createSelector(
  selectAuditHistoryState,
  (s) => s.totalCount
);

export const selectAuditHistoryPage = createSelector(
  selectAuditHistoryState,
  (s) => s.pageNumber
);

export const selectAuditHistoryHasMore = createSelector(
  selectAuditHistoryState,
  (s) => s.records.length < s.totalCount
);

// ─── Campaign Communication History Selectors ───────────────────────────────

export const selectCommunicationHistoryState = createSelector(
  selectCampaignFeature,
  (state) => state.communicationHistory
);

export const selectCommunicationHistoryRecords = createSelector(
  selectCommunicationHistoryState,
  (s) => s.records
);

export const selectCommunicationHistoryLoading = createSelector(
  selectCommunicationHistoryState,
  (s) => s.loading
);

export const selectCommunicationHistoryError = createSelector(
  selectCommunicationHistoryState,
  (s) => s.error
);

export const selectCommunicationHistoryHasMore = createSelector(
  selectCommunicationHistoryState,
  (s) => s.hasMore
);

export const selectCommunicationHistoryCursor = createSelector(
  selectCommunicationHistoryState,
  (s) => s.cursor
);

export const selectCommunicationHistoryTotalCount = createSelector(
  selectCommunicationHistoryState,
  (s) => s.matchingRecordCount
);

export const selectCommunicationHistorySelectedRecord = createSelector(
  selectCommunicationHistoryState,
  (s) => s.selectedRecord
);

export const selectCommunicationHistoryHasSearched = createSelector(
  selectCommunicationHistoryState,
  (s) => s.hasSearched
);

export const selectCommunicationDetail = createSelector(
  selectCommunicationHistoryState,
  (s) => s.communicationDetail
);

export const selectCommunicationDetailLoading = createSelector(
  selectCommunicationHistoryState,
  (s) => s.detailLoading
);

export const selectCommunicationDetailError = createSelector(
  selectCommunicationHistoryState,
  (s) => s.detailError
);

export const selectCommunicationHistoryChannel = createSelector(
  selectCommunicationHistoryState,
  (s) => s.channel
);

export const selectCommunicationHistoryContact = createSelector(
  selectCommunicationHistoryState,
  (s) => s.contact
);

export const selectCommunicationHistoryFilters = createSelector(
  selectCommunicationHistoryState,
  (s) => s.filters
);

export const selectCommunicationHistorySortOrder = createSelector(
  selectCommunicationHistoryState,
  (s) => s.sortOrder
);

// ─── Phase 3: Campaign Workflow Selectors ───────────────────────────────────

export const selectCampaignWorkflow = createSelector(
  selectCampaignFeature,
  (state) => state.workflow
);

export const selectWorkflowTabData = createSelector(
  selectCampaignWorkflow,
  (workflow) => workflow.workflowTabData
);

export const selectCommunicationNodeData = createSelector(
  selectCampaignWorkflow,
  (workflow) => workflow.communicationNodeData
);

export const selectActiveCommunicationId = createSelector(
  selectCampaignWorkflow,
  (workflow) => workflow.activeCommunicationId
);

export const selectNodeLabelUpdate = createSelector(
  selectCampaignWorkflow,
  (workflow) => workflow.nodeLabelUpdate
);

export const selectFitNetworkTrigger = createSelector(
  selectCampaignWorkflow,
  (workflow) => workflow.fitNetworkTrigger
);

/** Returns communication data for a specific node */
export const selectNodeCommunicationData = (nodeId: string) => createSelector(
  selectCommunicationNodeData,
  (data) => data ? data[nodeId] : null
);

// ─── Phase 4: Import Patient Selectors ─────────────────────────────────────

export const selectCampaignImport = createSelector(
  selectCampaignFeature,
  (state) => state.import
);

export const selectFileExistOnRedis = createSelector(
  selectCampaignImport,
  (importState) => importState.fileExistOnRedis
);

export const selectFileId = createSelector(
  selectCampaignImport,
  (importState) => importState.fileId
);

export const selectIsEmailOrPhonePresent = createSelector(
  selectCampaignImport,
  (importState) => importState.isEmailOrPhonePresent
);

export const selectIsFileValidating = createSelector(
  selectCampaignImport,
  (importState) => importState.isFileValidating
);

export const selectIsDataMappingGridPopulated = createSelector(
  selectCampaignImport,
  (importState) => importState.isDataMappingGridPopulated
);

export const selectRecordsData = createSelector(
  selectCampaignImport,
  (importState) => importState.recordsData
);

export const selectImportUnsavedChanges = createSelector(
  selectCampaignImport,
  (importState) => importState.hasUnsavedChanges
);

export const selectFileUploadCompleted = createSelector(
  selectCampaignImport,
  (importState) => importState.fileUploadCompleted
);

// ─── Phase 5: Unsubscribe Import Selectors ─────────────────────────────────

export const selectUsubImportState = createSelector(
  selectCampaignFeature,
  (state) => state.usubImport
);

export const selectUsubImportMode = createSelector(
  selectUsubImportState,
  (state) => state.mode
);

export const selectUsubImportListType = createSelector(
  selectUsubImportState,
  (state) => state.listType
);

export const selectUsubImportSelectedDomain = createSelector(
  selectUsubImportState,
  (state) => state.selectedDomain
);

export const selectUsubImportFileId = createSelector(
  selectUsubImportState,
  (state) => state.fileId
);

export const selectUsubImportFileNameWithId = createSelector(
  selectUsubImportState,
  (state) => state.fileNameWithId
);

export const selectUsubImportFileValidating = createSelector(
  selectUsubImportState,
  (state) => state.isFileValidating
);

export const selectUsubImportRequiredColumnMapped = createSelector(
  selectUsubImportState,
  (state) => state.isEmailOrPhonePresent
);

export const selectUsubImportDataMappingGridPopulated = createSelector(
  selectUsubImportState,
  (state) => state.isDataMappingGridPopulated
);

export const selectUsubImportRecordsData = createSelector(
  selectUsubImportState,
  (state) => state.recordsData
);

export const selectUsubImportUploadCompleted = createSelector(
  selectUsubImportState,
  (state) => state.fileUploadCompleted
);

export const selectUsubImportHeartbeatActive = createSelector(
  selectUsubImportState,
  (state) => state.heartbeatActive
);

export const selectUsubImportLoading = createSelector(
  selectUsubImportState,
  (state) => state.loading
);

export const selectUsubImportError = createSelector(
  selectUsubImportState,
  (state) => state.error
);

export const selectUsubImportSuccessMessage = createSelector(
  selectUsubImportState,
  (state) => state.successMessage
);

// ─── Phase 4: Unsubscribe Management Selectors ─────────────────────────────

export const selectUnsubscribeState = createSelector(
  selectCampaignFeature,
  (state) => state.unsubscribe
);

export const selectUnsubscribeActiveTab = createSelector(
  selectUnsubscribeState,
  (state) => state.activeTab
);

export const selectUnsubscribeLoading = createSelector(
  selectUnsubscribeState,
  (state) => state.loading
);

export const selectUnsubscribeError = createSelector(
  selectUnsubscribeState,
  (state) => state.error
);

export const selectUnsubscribeExportDisabled = createSelector(
  selectUnsubscribeState,
  (state) => state.exportDisabled
);

export const selectUnsubscribeExportError = createSelector(
  selectUnsubscribeState,
  (state) => state.exportError
);

export const selectUnsubscribeResubscribing = createSelector(
  selectUnsubscribeState,
  (state) => state.resubscribing
);

export const selectUnsubscribeLastSuccessMessage = createSelector(
  selectUnsubscribeState,
  (state) => state.lastSuccessMessage
);

export const selectUnsubscribeLastResubscribeType = createSelector(
  selectUnsubscribeState,
  (state) => state.lastResubscribeType
);

export const selectUnsubscribeDomains = createSelector(
  selectUnsubscribeState,
  (state) => state.domains
);

export const selectUnsubscribeDomainsLoading = createSelector(
  selectUnsubscribeState,
  (state) => state.domainsLoading
);

export const selectUnsubscribeActivePhoneNumber = createSelector(
  selectUnsubscribeState,
  (state) => state.activePhoneNumber
);

export const selectUnsubscribeRecords = (listType: 'email' | 'sms') => createSelector(
  selectUnsubscribeState,
  (state) => listType === 'email' ? state.emailRecords : state.smsRecords
);

export const selectUnsubscribeTotal = (listType: 'email' | 'sms') => createSelector(
  selectUnsubscribeState,
  (state) => listType === 'email' ? state.emailTotal : state.smsTotal
);

export const selectUnsubscribePage = (listType: 'email' | 'sms') => createSelector(
  selectUnsubscribeState,
  (state) => listType === 'email' ? state.emailPage : state.smsPage
);

export const selectUnsubscribeSorting = (listType: 'email' | 'sms') => createSelector(
  selectUnsubscribeState,
  (state) => listType === 'email' ? state.emailSorting : state.smsSorting
);

export const selectUnsubscribeSearch = (listType: 'email' | 'sms') => createSelector(
  selectUnsubscribeState,
  (state) => listType === 'email' ? state.emailSearch : state.smsSearch
);

export const selectUnsubscribeDateRange = (listType: 'email' | 'sms') => createSelector(
  selectUnsubscribeState,
  (state) => listType === 'email'
    ? { startDate: state.emailStartDate, endDate: state.emailEndDate }
    : { startDate: state.smsStartDate, endDate: state.smsEndDate }
);

export const selectUnsubscribeSelectedDomain = createSelector(
  selectUnsubscribeState,
  (state) => state.emailSelectedDomain
);

export const selectUnsubscribePagination = (listType: 'email' | 'sms') => createSelector(
  selectUnsubscribePage(listType),
  selectUnsubscribeTotal(listType),
  (page, totalCount) => {
    const limit = 10;
    const totalPages = Math.ceil(totalCount / limit);
    const pageObject: { pageNum: number; selected: boolean }[] = [];

    if (totalCount > 1) {
      if (page > 1) {
        pageObject.push({ pageNum: page - 1, selected: false });
      }
      pageObject.push({ pageNum: page, selected: false });
      if (page < totalPages) {
        pageObject.push({ pageNum: page + 1, selected: false });
      }
      if (page === 1) {
        if (pageObject.length > 0) pageObject[0].selected = true;
      } else {
        if (pageObject.length > 1) pageObject[1].selected = true;
      }
    }

    return { page, totalPages, totalCount, pageObject, limit };
  }
);
